import React, { useState, useEffect } from "react";

import { isEmpty } from "lodash";
import { Trans, useTranslation } from "react-i18next";

import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import GroupedMaterialsTable from "../../components/Tables/GroupedMaterialsTable";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import Pagination from "../../components/Pagination/Pagination";
import MaterialGroupsAPI from "../../API/materialGroups.api";
import MaterialsAPI from "../../API/materials.api";
import LookupsAPI from "../../API/lookups.api";
import MaterialModal from "../../components/Modals/MaterialModal";
import MaterialGroupModal from "../../components/Modals/MaterialGroupModal";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import { getSessionFilters } from "../../utils/FilterHelpers";
import { getRoles } from "../../utils/OktaHelpers";
import { isAuthorized } from "../../utils/RolesHelpers";
import { ROLES } from "../../utils/constants";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { setObject, getObject } from "../../utils/StorageHelpers";
import { mapLookupToSelect } from "../../utils/Helpers";
import MissingData from "../../components/Shared/MissingData";
import { ACTIONS, ENTITIES } from "../../utils/LogManagement/LogConstants";

const MaterialGroups = () => {
    const [materialModal, setMaterialModal] = useState(false);
    const [isCreateMaterial, setIsCreateMaterial] = useState(true);
    const [groupsModal, setGroupsModal] = useState(false);
    const [isCreateGroup, setIsCreateGroup] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const [filters, setFilters] = useState({});
    //React lazy initialization in to reduce the cost of useless calling of getSessionFilters
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("material-groups-filters")
    );
    const [filtersUsed, setFiltersUsed] = useState(false);
    const [sortBy] = useState(null);
    const [sortDirection] = useState(null);
    const [organizationLookup, setOrganizationLookup] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [data, setData] = useState([]);
    const [materialToBeDeleted, setMaterialToBeDeleted] = useState(null);
    const [groupToBeDeleted, setGroupToBeDeleted] = useState(null);

    const [materialToBeEdited, setMaterialToBeEdited] = useState(null);
    const [groupToBeEdited, setGroupToBeEdited] = useState(null);

    const [groupsLookup, setGroupsLookup] = useState([]);

    const { t } = useTranslation();

    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Material-Groups-itemsPerPage", true) || 10
    );

    const headers = [
        { label: t("name"), skipSort: true },
        { label: t("description"), skipSort: true },
        { label: t("createdAt"), skipSort: true },
        { label: t("createdBy"), skipSort: true },
        {
            label: t("affiliate"),
            skipSort: true,
        },
        {
            label: t("actions"),
            skipSort: true,
            role: !isAuthorized(
                [ROLES.support, ROLES.partnerAdmin],
                getRoles()
            ),
        },
    ];

    const subHeaders = [
        { label: t("materialName"), skipSort: true },
        { label: t("technicalName"), skipSort: true },
        { label: t("materialDescription"), skipSort: true },
        { label: t("createdAt"), skipSort: true },
        { label: t("createdBy"), skipSort: true },
        {
            label: t("actions"),
            skipSort: true,
            role: !isAuthorized(
                [ROLES.support, ROLES.partnerAdmin],
                getRoles()
            ),
        },
    ];

    const filterOptions = [
        {
            label: t("groupName"),
            name: "name",
            type: "Input",
            placeholder: t("materialGroupNamePlaceholder"),
        },
        {
            label: t("materialName"),
            name: "materialName",
            type: "Input",
            placeholder: t("materialNamePlaceholder"),
        },
        {
            label: t("affiliate"),
            name: "organizationId",
            type: "Dropdown",
            options: organizationLookup,
        },
    ];

    useEffect(() => {
        const localStorageFilters = getSessionFilters(
            "material-groups-filters"
        );

        if (!isEmpty(localStorageFilters)) {
            fetchGroups(localStorageFilters);
            setFilters(localStorageFilters);
            setAppliedFilters(localStorageFilters);
        } else {
            fetchGroups();
        }
        fetchOrganizations();
    }, []);

    const fetchOrganizations = async () => {
        const result = await LookupsAPI.getOrganizations();
        setOrganizationLookup(mapLookupToSelect(result) || []);
    };

    const fetchGroups = async (
        _filters,
        _sortBy = null,
        _sortDirection = null,
        offset = 0,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsLoading(true);
        const _offset = offset < 0 ? 0 : offset;

        const result = await MaterialGroupsAPI.listMaterialGroups(
            itemsPerPage,
            _filters,
            _offset,
            _sortBy,
            _sortDirection
        );
        setTotalCount(result?.totalCount || 0);
        setData(result?.data || []);
        setIsLoading(false);
    };

    const fetchGroupsLookup = async () => {
        const result = await LookupsAPI.getMaterialGroups();
        setGroupsLookup(result || []);
    };

    const handleCreatedMaterialClicked = () => {
        setIsCreateMaterial(true);
        setMaterialModal(true);
    };

    const handleCreatedGroupClicked = () => {
        setIsCreateGroup(true);
        setGroupsModal(true);
    };

    const applyFilters = (specificApplyFilters) => {
        fetchGroups(specificApplyFilters, sortBy, sortDirection);
        setFiltersUsed(true);
    };

    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            fetchGroups(appliedFilters, sortBy, sortDirection, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Material-Groups-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        fetchGroups(appliedFilters, sortBy, sortDirection, 0, itemsPerPage);
    };

    const handleDeleteMaterial = async () => {
        try {
            const result = await MaterialsAPI.deleteMaterial(
                materialToBeDeleted.id
            );
            if (!isEmpty(result)) {
                fetchGroups(appliedFilters, sortBy, sortDirection);
                SuccessAlert(t("deleteMaterialSuccessMessage"));
                LogCapture.info(
                    `Material deleted with id: ${materialToBeDeleted?.id}`,
                    ENTITIES.materials,
                    ACTIONS.materials.delete
                );
                return true;
            } else {
                return false;
            }
        } catch (error) {
            LogCapture.error(
                `Couldn't delete material with id: ${materialToBeDeleted?.id}, error: ${error}`
            );
            ErrorAlert(error);
        }
    };

    const handleDeleteGroup = async () => {
        try {
            const result = await MaterialGroupsAPI.removeMaterialGroup(
                groupToBeDeleted.id
            );
            if (!isEmpty(result)) {
                LogCapture.info(
                    `Material group deleted with id: ${groupToBeDeleted?.id}`,
                    ENTITIES.materialGroups,
                    ACTIONS.materialGroups.delete
                );
                completionHandler();
                SuccessAlert(t("deleteGroupSuccessMessage"));
                return true;
            } else {
                return false;
            }
        } catch (error) {
            LogCapture.error(
                `Couldn't delete material group with id: ${groupToBeDeleted?.id}, error: ${error}`
            );
            ErrorAlert(error);
        }
    };

    const completionHandler = () => {
        //TODO: Clear groups array
        let newCurrentPage = currentPage;
        if (data.length <= 1 && currentPage > 1) {
            newCurrentPage = currentPage - 1;
        }

        fetchGroups(
            appliedFilters,
            sortBy,
            sortDirection,
            newCurrentPage - 1
        ).then(() => {
            setCurrentPage(newCurrentPage);
        });
        fetchGroupsLookup();
    };

    const hasCreateGroupAccess = () => {
        return isAuthorized([ROLES.support, ROLES.partnerAdmin], getRoles());
    };

    return (
        <>
            <div id="model-content" className="model-content">
                <header className="main-content-header flex items-center justify-between">
                    <h1 className="h1">
                        <Trans i18nKey="materialGroups">Material Groups</Trans>
                    </h1>
                    <div className="action-header-wrapper flex items-center">
                        <FilterDropdown
                            filterOptions={filterOptions}
                            filters={filters}
                            setFilters={setFilters}
                            applyFilters={applyFilters}
                            component="material-groups"
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                            disabled={isLoading}
                        />
                        {isAuthorized(
                            [
                                ROLES.partnerAdmin,
                                ROLES.support,
                                ROLES.partnerUser,
                                ROLES.admin,
                                ROLES.user,
                            ],
                            getRoles()
                        ) && (
                            <>
                                <button
                                    className="default-orange-outline-btn ml-2"
                                    onClick={() => {
                                        handleCreatedMaterialClicked();
                                    }}
                                >
                                    <i className="fas fa-plus mr-2" />
                                    <Trans i18nKey="addNewMaterial">
                                        New Material
                                    </Trans>
                                </button>

                                {hasCreateGroupAccess() && (
                                    <button
                                        className="default-orange-outline-btn ml-2"
                                        onClick={handleCreatedGroupClicked}
                                    >
                                        <i className="fas fa-plus mr-2" />
                                        <Trans i18nKey="addNewGroup">
                                            New Group
                                        </Trans>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </header>
            </div>
            <LoadingOverlay active={isLoading} text="Loading...">
                <div className="card-table shadow">
                    <div
                        className="tabs-container active"
                        id="tabs-content-all"
                    >
                        {!isEmpty(data) ? (
                            <>
                                <GroupedMaterialsTable
                                    headers={headers}
                                    subHeaders={subHeaders}
                                    data={data}
                                    setMaterialModal={setMaterialModal}
                                    setIsCreateMaterial={setIsCreateMaterial}
                                    setGroupsModal={setGroupsModal}
                                    setIsCreateGroup={setIsCreateGroup}
                                    handleDeleteMaterial={handleDeleteMaterial}
                                    handleDeleteGroup={handleDeleteGroup}
                                    materialToBeDeleted={materialToBeDeleted}
                                    setMaterialToBeDeleted={
                                        setMaterialToBeDeleted
                                    }
                                    groupToBeDeleted={groupToBeDeleted}
                                    setGroupToBeDeleted={setGroupToBeDeleted}
                                    setMaterialToBeEdited={
                                        setMaterialToBeEdited
                                    }
                                    setGroupToBeEdited={setGroupToBeEdited}
                                />
                                <Pagination
                                    currentPage={currentPage}
                                    setCurrentPage={handlePageClick}
                                    totalCount={totalCount}
                                    sizePerPage={itemsPerRequest}
                                    handleItemsPerPage={handleItemsPerPage}
                                />
                            </>
                        ) : (
                            <MissingData
                                isFilterUsed={filtersUsed}
                                entity={t("materialGroups")}
                                includeText={!isLoading}
                                path="/options/material-groups"
                            />
                        )}
                    </div>
                </div>
            </LoadingOverlay>
            <MaterialModal
                modalButton={materialModal}
                modalButtonSetter={setMaterialModal}
                completionHandler={completionHandler}
                groupsLookup={groupsLookup}
                {...(!isCreateMaterial && {
                    ...materialToBeEdited,
                })}
            />
            <MaterialGroupModal
                modalButton={groupsModal}
                modalButtonSetter={setGroupsModal}
                completionHandler={completionHandler}
                {...(!isCreateGroup && {
                    ...groupToBeEdited,
                })}
            />
        </>
    );
};

export default MaterialGroups;
